import React, { useState } from "react";
import { IoMdEye } from "react-icons/io";
import { RiImageAddFill } from "react-icons/ri";
import { AiOutlineClose } from "react-icons/ai";
import { Link } from "react-router-dom";
import Pagination from "../../../components/common/Pagination";
import {
  getAllOffers,
  addOfferForAll,
  updateVisibility,
} from "../../../api/admin/offers";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useTranslation } from "../../../hooks/useTranslation";

const PopupModal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed sm:inset-0 bottom-0 left-0 right-0 bg-black bg-opacity-50 flex sm:items-center items-end justify-center z-50 h-full">
      <div className="bg-white sm:rounded-[20px] rounded-t-[20px] w-full sm:w-[845px] relative sm:shadow-lg">
        <button
          onClick={onClose}
          className="absolute top-[33px] right-[30px] text-[#9E9E9E] hover:text-black"
        >
          ✕
        </button>
        {children}
      </div>
    </div>
  );
};

const PromotionList = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState("hotels");
  const [searchQuery, setSearchQuery] = useState({
    hotels: true,
    restaurants: false,
    addedByAdmins: false,
    addedByOwners: false,
  });
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const limit = 10;
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    discountPercentage: "",
    startDate: "",
    endDate: "",
    offerForAllHotels: true,
    offerForAllRestaurants: false,
    image: null,
    imagePreview: null,
  });

  const [errors, setErrors] = useState({
    title: "",
    description: "",
    discountPercentage: "",
    startDate: "",
    endDate: "",
    image: "",
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["offers", page, searchQuery],
    queryFn: () => getAllOffers({ page, limit, ...searchQuery }),
    keepPreviousData: true,
  });

  const offers = data?.data?.offers || [];
  const pagination = data?.data?.pagination || {};

  const { mutate: addOfferMutation , isPending: isAdding } = useMutation({
    mutationFn: addOfferForAll,
    onSuccess: () => {
      queryClient.invalidateQueries(["offers"]);
      closeModal();
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("offers").errors.addNew;
      toast.error(errorMessage);
    },
  });

  const { mutate: updateVisibilityMutation , isPending: isUpdating } = useMutation({
    mutationFn: ({ id, currentValue }) =>
      updateVisibility(id, { visibility: !currentValue }),
    onSuccess: (data, variables) => {
      const newVisibility = !variables.currentValue;
      const successMessage = newVisibility
        ? t("offers").successMessages?.visibilityEnabled || "Offer visibility enabled successfully!"
        : t("offers").successMessages?.visibilityDisabled || "Offer visibility disabled successfully!";
      toast.success(successMessage, {
        duration: 4000,
        position: "top-center",
      });
      queryClient.invalidateQueries(["offers"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message || t("offers").errors?.visibility || "Failed to update offer visibility.";
      toast.error(errorMessage);
    },
  });

  const closeModal = () => {
    setIsPopupOpen(false);
    setFormData({
      title: "",
      description: "",
      discountPercentage: "",
      startDate: "",
      endDate: "",
      offerForAllHotels: true,
      offerForAllRestaurants: false,
      image: null,
      imagePreview: null,
    });
  };

  const validateForm = (data) => {
    const newErrors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const startDate = new Date(data.startDate);
    const endDate = new Date(data.endDate);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);

    if (!data.title) newErrors.title = t("offers").errors.titleRequired;

    if (!data.description)
      newErrors.description = t("offers").errors.descriptionRequired;

    if (!data.discountPercentage) {
      newErrors.discountPercentage = t("offers").errors.discountRequired;
    } else {
      const discount = parseFloat(data.discountPercentage);
      if (isNaN(discount) || discount < 0 || discount > 100) {
        newErrors.discountPercentage = t("offers").errors.discountRange;
      }
    }

    if (!data.startDate) {
      newErrors.startDate = t("offers").errors.startDateRequired;
    } else if (startDate < today) {
      newErrors.startDate = t("offers").errors.startDatePast;
    }

    if (!data.endDate) {
      newErrors.endDate = t("offers").errors.endDateRequired;
    } else if (endDate < startDate) {
      newErrors.endDate = t("offers").errors.endDateBeforeStart;
    }

    // Image validation - check for File object, URL string, or preview
    const hasImageFile = data.image instanceof File;
    const hasImageUrl = data.image && typeof data.image === 'string' && data.image.trim() !== '';
    const hasImagePreview = !!data.imagePreview;
    const hasImage = hasImageFile || hasImageUrl || hasImagePreview;
    
    if (!hasImage) {
      newErrors.image = t("offers").errors.imageRequired;
    }
    return newErrors;
  };

  const handleAddOffer = () => {
    const newErrors = validateForm(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    const payload = new FormData();

    Object.entries(formData).forEach(([key, value]) => {
      if (key === "offerForAllHotels" || key === "offerForAllRestaurants") {
        payload.append(key, Boolean(value));
      } else {
        payload.append(key, value);
      }
    });

    addOfferMutation(payload);
  };

  const validateField = (fieldName, value) => {
    const updatedFormData = { ...formData, [fieldName]: value };
    const newErrors = validateForm(updatedFormData);
    setErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: newErrors[fieldName] || "",
    }));
  };

  const handleToggle = (value) => {
    setSelected(value);

    setSearchQuery((prev) => ({
      ...prev,
      hotels: value === t("offers").byOwners,
      restaurants: value === t("offers").byAdmin,
    }));

    setFormData((prev) => ({
      ...prev,
      offerForAllHotels: value === t("offers").byOwners,
      offerForAllRestaurants: value === t("offers").byAdmin,
    }));
  };

  const openModel = () => {
    if (!formData.offerForAllHotels && !formData.offerForAllRestaurants) {
      toast.error(t("offers").selectOption);
      return;
    }
    setIsPopupOpen(true);
  };

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      setFormData((prev) => ({ ...prev, [name]: files[0] }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } else if (name === "offerTarget") {
      setFormData((prev) => ({
        ...prev,
        offerForAllHotels: value === "for-hotels",
        offerForAllRestaurants: value === "for-restaurants",
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }

    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <>
      <div className="xl:flex md:gap-[30px] gap-3 mt-[30px] justify-between flex-wrap">
        <div className="flex gap-[16px] items-center  flex-wrap md:flex-nowrap  lg:mb-0 mb-3">
          <select
            className="w-full max-w-[264px] bg-white p-[11px] border border-[#DDDDDD] rounded-[5px] focus:outline-none focus:border-[#4CAF50] font-medium text-[17px]"
            defaultValue=""
            onChange={(e) => {
              const value = e.target.value;
              if (value === "offers-posted-by-owners") {
                setSearchQuery((prev) => ({
                  ...prev,
                  addedByOwners: true,
                  addedByAdmins: false,
                }));
              } else if (value === "offers-posted-by-admin") {
                setSearchQuery((prev) => ({
                  ...prev,
                  addedByAdmins: true,
                  addedByOwners: false,
                }));
              } else {
                setSearchQuery((prev) => ({
                  ...prev,
                  addedByAdmins: false,
                  addedByOwners: false,
                }));
              }
              setPage(1);
            }}
          >
            <option value="" disabled>
              {t("offers").selectOption}
            </option>
            <option value="offers-posted-by-owners">
              {t("offers").byOwners}
            </option>
            <option value="offers-posted-by-admin">
              {t("offers").byAdmin}
            </option>
          </select>

          <div className="xl:max-w-[282px] max-w-[200px] w-full">
            <div className="flex items-center bg-[#D4EFD5] border border-[#4CAF50] rounded-full md:overflow-hidden w-full ">
              <button
                className={`basis-1/2 lg:px-[18px] lg:py-[8px] md:py-3 md:px-[20px] p-4 rounded-full text-base transition-all font-semibold ${
                  selected === "hotels"
                    ? "bg-[#4CAF50] text-white"
                    : "bg-transparent text-black"
                }`}
                onClick={() => {
                  handleToggle("hotels");
                  setPage(1);
                }}
              >
                {t("offers").Hotel}
              </button>
              <button
                className={`basis-1/2 lg:px-[18px] lg:py-[8px] md:py-3 md:px-[20px] p-4 rounded-full text-base transition-all font-semibold ${
                  selected === "restaurants"
                    ? "bg-[#4CAF50] text-white"
                    : "bg-transparent text-black"
                }`}
                onClick={() => {
                  handleToggle("restaurants");
                  setPage(1);
                }}
              >
                {t("offers").Restaurant}
              </button>
            </div>
          </div>
        </div>
        <div className="flex items-center md:gap-[20px] gap-3 xl:justify-end flex-1 flex-wrap">
          <h2 className="2xl:text-[22px] text-base font-semibold">
            {t("offers").createSpecial}
          </h2>

          <select
            name="offerTarget"
            value={
              formData.offerForAllHotels
                ? "for-hotels"
                : formData.offerForAllRestaurants
                ? "for-restaurants"
                : ""
            }
            onChange={handleChange}
            className="w-full max-w-[250px] bg-white p-[11px] border border-[#DDDDDD] rounded-[5px] focus:outline-none focus:border-[#4CAF50] font-medium 2xl:text-[17px] text-sm"
          >
            <option value="" disabled>
              {t("offers").selectOption}
            </option>
            <option value="for-hotels">{t("offers").Hotel}</option>
            <option value="for-restaurants">{t("offers").Restaurant}</option>
          </select>

          <button
            onClick={openModel}
            className="inline-block w-full 2xl:min-w-[205px] max-w-max rounded-[2px] bg-[#4CAF50] 2xl:text-[18px] text-sm text-white text-center font-semibold p-[9px]  green-shadow hover:bg-[#209b25]"
          >
            {t("offers").createNew}
          </button>
        </div>
      </div>

      <div className="lg:mt-[60px] mt-[20px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white">
          <h2 className="text-base font-semibold">
            {selected === "restaurants" ? "" : ""} {t("offers").hotelOffer}
          </h2>

          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <p className="text-red-500 text-center mt-6">{error.message}</p>
          ) : (
            <div className="overflow-x-auto md:mt-[30px] mt-[30px]">
              <table className="min-w-full  ">
                <thead className="bg-[#FFFAF2] ">
                  <tr className="">
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {selected === "restaurants" ? "" : ""}{" "}
                      {/* {t("offers").restaurantName} */}
                      {selected === "restaurants"
                        ? t("offers").restaurantName
                        : t("offers").hotelName}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").title}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").description}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").discount}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").duration}
                    </th>{" "}
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").status}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").actions}
                    </th>
                    <th className="text-left text-lg font-medium text-[#000000] py-[24px] px-[20px] ">
                      {t("offers").visibility}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {offers?.map((offer) => {
                    const description = offer.description
                      ? offer.description.split(" ").slice(0, 5).join(" ") +
                        "..."
                      : "";
                    return (
                      <tr key={offer._id} className="border-t border-[#E4E4E4]">
                        {selected === "restaurants" ? (
                          <td className="py-[20px] px-[20px] text-base font-normal text-black">
                            {offer.restaurantId?.name || "All Restaurants"}
                          </td>
                        ) : (
                          <td className="py-[20px] px-[20px] text-base font-normal text-black">
                            {offer.hotelId?.name || "All Hotels"}
                          </td>
                        )}

                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {offer.title}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {description}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {offer.discountPercentage}%
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          {new Date(offer.startDate).toLocaleDateString()} -{" "}
                          {new Date(offer.endDate).toLocaleDateString()}
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          <div
                            className={`p-[8px] rounded-full max-w-[129px] text-center text-sm text-white ${
                              offer.status === "pending"
                                ? "bg-[#FF9D00]"
                                : offer.status === "approved"
                                ? "bg-green-600"
                                : "bg-red-500"
                            }`}
                          >
                            {offer.status.charAt(0).toUpperCase() +
                              offer.status.slice(1)}
                          </div>
                        </td>
                        <td className="py-[20px] px-[20px]">
                          <Link
                            to={`/promotiondetail/${offer._id}`}
                            className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                          >
                            <IoMdEye className="text-base text-black" />
                          </Link>
                        </td>
                        <td className="py-[20px] px-[20px] text-base font-normal text-black">
                          <div
                            onClick={() =>
                              updateVisibilityMutation({
                                id: offer._id,
                                currentValue: offer.visibility,
                              })
                            }
                            className={`w-[48px] h-[20px] mx-auto flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                              offer.visibility ? "bg-[#4CAF50]" : "bg-[#e1e1e1]"
                            }`}
                          >
                            <div
                              className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                                offer.visibility
                                  ? "translate-x-6"
                                  : "translate-x-0"
                              }`}
                            />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {offers.length > 0 && (
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          )}
        </div>
      </div>

      <PopupModal isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
        <div className="w-full rounded-[20px] sm:p-[30px] p-[15px]">
          <h2 className="text-[22px] font-medium mb-[19px]">
            {t("offers").addNew}{" "}
            {formData.offerForAllHotels
              ? t("offers").hotelOffer
              : t("offers").restaurantOffer}
          </h2>

          <div className="overflow-y-auto h-[350px]">
            <div className="mb-[16px]">
              <label className="text-sm text-black mb-1 block">
                {t("offers").form.title} *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => {
                  const value = e.target.value;
                  setFormData({ ...formData, title: value });
                  validateField("title", value);
                }}
                placeholder={t("offers").form.titlePlaceholder}
                className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                  errors.title ? "border-red-500" : "border-[#DDDDDD]"
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-sm mt-1">{errors.title}</p>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-[16px] mb-[16px]">
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("offers").form.startDate} *
                </label>
                <input
                  type="date"
                  value={formData.startDate}
                  min={new Date().toISOString().split("T")[0]}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, startDate: value });
                    validateField("startDate", value);
                  }}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.startDate ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.startDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.startDate}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("offers").form.endDate} *
                </label>
                <input
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, endDate: value });
                    validateField("endDate", value);
                  }}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.endDate ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.endDate && (
                  <p className="text-red-500 text-sm mt-1">{errors.endDate}</p>
                )}
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-[16px] mb-[16px]">
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("offers").form.discount} *
                </label>
                <input
                  type="number"
                  value={formData.discountPercentage}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, discountPercentage: value });
                    validateField("discountPercentage", value);
                  }}
                  placeholder={t("offers").form.discountPlaceholder}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none ${
                    errors.discountPercentage
                      ? "border-red-500"
                      : "border-[#DDDDDD]"
                  }`}
                />
                {errors.discountPercentage && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.discountPercentage}
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm text-black mb-1 block">
                  {t("offers").form.description} *
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFormData({ ...formData, description: value });
                    validateField("description", value);
                  }}
                  placeholder={t("offers").form.description}
                  className={`border rounded-[10px] py-[13px] px-[16px] w-full focus:outline-none h-[52px] ${
                    errors.description ? "border-red-500" : "border-[#DDDDDD]"
                  }`}
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.description}
                  </p>
                )}
              </div>
            </div>

            <div className="w-full max-w-[200px] mx-auto">
              <div className={`bg-white p-5 border-dashed border-2 rounded-[10px] text-center relative w-full h-[120px] flex flex-col items-center justify-center cursor-pointer ${
                errors.image ? "border-red-500" : "border-[#DDDDDD]"
              }`}>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      const imagePreview = URL.createObjectURL(file);
                      setFormData({ ...formData, image: file, imagePreview });
                      validateField("image", file);
                    }
                  }}
                  className="opacity-0 absolute top-0 left-0 right-0 bottom-0 w-full h-full cursor-pointer"
                />
                {formData.imagePreview ? (
                  <>
                    <img
                      src={formData.imagePreview}
                      alt="Selected"
                      className="w-full h-full object-cover rounded-[10px]"
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (formData.imagePreview && formData.imagePreview.startsWith('blob:')) {
                          URL.revokeObjectURL(formData.imagePreview);
                        }
                        setFormData({ ...formData, image: null, imagePreview: null });
                        validateField("image", null);
                      }}
                      className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg z-20 flex items-center justify-center transition-colors"
                      aria-label="Remove selected image"
                      title="Remove image"
                    >
                      <AiOutlineClose size={18} />
                    </button>
                  </>
                ) : (
                  <>
                    <RiImageAddFill size={24} className="text-[#8C8C8C]" />
                    <p className="text-sm font-medium mt-[14px]">
                      {t("offers").form.addImage}
                    </p>
                  </>
                )}
              </div>
              {errors.image && (
                <p className="text-red-500 text-sm mt-2 text-center font-medium">{errors.image}</p>
              )}
            </div>
          </div>

          <div className="flex gap-[30px] justify-end pt-[23px] border-t border-[#EEEEEE]">
            <button
              onClick={() => setIsPopupOpen(false)}
              className="text-white font-bold py-[14px] px-[31px] rounded-[2px] bg-[#CCCCCC] hover:bg-[#B8B8B8] text-[20px] text-center gray-shadow"
            >
              {t("common").cancel}
            </button>
            <button
              onClick={handleAddOffer}
              disabled={isAdding || isUpdating}
              className="rounded-[2px] bg-[#4CAF50] text-base text-white font-bold py-[14px] px-[30px] green-shadow hover:bg-[#209b25]"
            >
              {(isAdding || isUpdating) ? t("header").loading : t("common").add}
            </button>
          </div>
        </div>
      </PopupModal>
    </>
  );
};

export default PromotionList;
